import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Class } from '@/types/classTypes';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Plus, Settings, Filter, Search, LayoutGrid, CalendarDays, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import AddScheduleDialog from '@/components/schedule/AddScheduleDialog';
import { useScheduleData } from '@/hooks/useScheduleData';
import { useScheduleConflicts } from '@/hooks/useScheduleConflicts';
import { useScheduleKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { KeyboardShortcutsHelp } from '@/components/ui/KeyboardShortcutsHelp';
import { AlertTriangle, AlertCircle, CheckCircle, HelpCircle } from 'lucide-react';
import WeeklyScheduleView from '@/components/schedule/views/WeeklyScheduleView';
import CalendarScheduleView from '@/components/schedule/views/CalendarScheduleView';
import MonthlyCalendarView from '@/components/schedule/views/MonthlyCalendarView';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Enhanced animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const floatingVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

type ViewType = 'weekly' | 'calendar' | 'monthly';

const Schedule: React.FC = () => {
  const [searchParams] = useSearchParams();
  const classId = searchParams.get('classId');
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id || '00000000-0000-0000-0000-000000000001';
  
  const [activeView, setActiveView] = useState<ViewType>('monthly');
  const [isAddScheduleOpen, setIsAddScheduleOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState<string>('all');
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  const {
    classes,
    schedules,
    enhancedClasses,
    isLoading,
    refetchSchedules
  } = useScheduleData(organizationId, classId);

  const currentClass = classId ? enhancedClasses.find(c => c.id === classId) : null;
  
  // Detect schedule conflicts for the overall schedule
  const { conflictSummary } = useScheduleConflicts(schedules);
  
  // Keyboard shortcuts
  const { shortcuts } = useScheduleKeyboardShortcuts(
    () => setIsAddScheduleOpen(true), // New schedule
    () => { // Navigate left
      if (activeView === 'weekly' || activeView === 'monthly') {
        // This will be handled by the individual view components
      }
    },
    () => { // Navigate right
      if (activeView === 'weekly' || activeView === 'monthly') {
        // This will be handled by the individual view components
      }
    },
    undefined, // Navigate up
    undefined, // Navigate down
    () => { // Escape
      if (isAddScheduleOpen) {
        setIsAddScheduleOpen(false);
      } else if (showKeyboardHelp) {
        setShowKeyboardHelp(false);
      }
    },
    undefined, // Toggle fullscreen (handled by MonthlyCalendarView)
    () => { // Refresh
      refetchSchedules();
      toast.success('Schedule refreshed');
    }
  );
  
  // Listen for help shortcut
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '?' && e.shiftKey) {
        setShowKeyboardHelp(true);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleScheduleUpdated = async () => {
    await refetchSchedules();
    return Promise.resolve();
  };
  
  const handleScheduleDeleted = async () => {
    await refetchSchedules();
    return Promise.resolve();
  };
  
  const handleScheduleAdded = async () => {
    await refetchSchedules();
    toast.success('Schedule added successfully');
    return Promise.resolve();
  };

  if (!organizationId) {
    toast.error('Missing organization ID. Please log in again.');
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
          <p className="text-gray-600">Loading your beautiful schedule...</p>
        </div>
      </div>
    );
  }

  const viewConfigs = [
    {
      id: 'weekly' as ViewType,
      name: 'Weekly Grid',
      icon: LayoutGrid,
      description: 'Grid view of weekly schedules',
      gradient: 'from-blue-500 to-purple-600'
    },
    {
      id: 'calendar' as ViewType,
      name: 'Calendar',
      icon: Calendar,
      description: 'Traditional calendar layout',
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'monthly' as ViewType,
      name: 'Monthly',
      icon: CalendarDays,
      description: 'Full month overview',
      gradient: 'from-orange-500 to-red-600'
    }
  ];

  const renderCurrentView = () => {
    const viewProps = {
      schedules,
      classes: enhancedClasses,
      onScheduleUpdated: handleScheduleUpdated,
      onScheduleDeleted: handleScheduleDeleted,
      initialClassId: classId
    };

    switch (activeView) {
      case 'weekly':
        return <WeeklyScheduleView {...viewProps} />;
      case 'calendar':
        return <CalendarScheduleView {...viewProps} />;
      case 'monthly':
        return <MonthlyCalendarView {...viewProps} />;
      default:
        return <MonthlyCalendarView {...viewProps} />;
    }
  };

  return (
    <TooltipProvider>
      <motion.div 
        className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/40"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Modern Floating Header */}
        <motion.div 
          variants={itemVariants}
          className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/60 shadow-sm"
        >
          <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              {/* Left: Title & Search */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-6 w-full sm:w-auto">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg sm:rounded-xl shadow-lg">
                    <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent truncate">
                        {currentClass ? currentClass.name : 'Schedule'}
                      </h1>
                      {/* Conflict Status Indicator */}
                      {conflictSummary.totalCount > 0 ? (
                        <div className={cn(
                          "flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium",
                          conflictSummary.errorCount > 0 
                            ? "bg-red-100 text-red-700" 
                            : "bg-yellow-100 text-yellow-700"
                        )}>
                          {conflictSummary.errorCount > 0 ? (
                            <AlertCircle className="h-3 w-3" />
                          ) : (
                            <AlertTriangle className="h-3 w-3" />
                          )}
                          <span>
                            {conflictSummary.errorCount > 0 
                              ? `${conflictSummary.errorCount} conflict${conflictSummary.errorCount !== 1 ? 's' : ''}` 
                              : `${conflictSummary.warningCount} warning${conflictSummary.warningCount !== 1 ? 's' : ''}`
                            }
                          </span>
                        </div>
                      ) : schedules.length > 0 ? (
                        <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3" />
                          <span>No conflicts</span>
                        </div>
                      ) : null}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                      {currentClass ? 'Class schedule management' : 'Manage your hive\'s schedule and events'}
                    </p>
                  </div>
                </div>
                
                {/* Search Bar - Enhanced Mobile */}
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search classes, events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full sm:w-56 md:w-64 bg-white/60 border-gray-200/60 focus:bg-white transition-all h-10 sm:h-9"
                  />
                </div>
              </div>

              {/* Right: Quick Actions - Better Mobile Layout */}
              <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-end">
                {/* Keyboard Shortcuts Help */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => setShowKeyboardHelp(true)}
                      variant="outline"
                      size="sm"
                      className="min-h-[40px] min-w-[40px] touch-manipulation p-2"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Keyboard shortcuts (Shift + ?)</p>
                  </TooltipContent>
                </Tooltip>
                {/* Class Filter - Enhanced for Mobile */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-white/60 border-gray-200/60 hover:bg-white min-h-[40px] touch-manipulation"
                    >
                      <Filter className="h-4 w-4 mr-1 sm:mr-2" />
                      <span className="hidden xs:inline">Filter</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setFilterClass('all')}>
                      All Classes
                    </DropdownMenuItem>
                    {enhancedClasses.map((cls) => (
                      <DropdownMenuItem key={cls.id} onClick={() => setFilterClass(cls.id)}>
                        {cls.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Add Schedule FAB - Enhanced Mobile */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={() => setIsAddScheduleOpen(true)}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 min-h-[40px] touch-manipulation"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                      <span className="hidden xs:inline sm:inline">Add Schedule</span>
                      <span className="inline xs:hidden sm:hidden">Add</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Create a new class schedule</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Beautiful View Switcher - Enhanced Mobile */}
        <motion.div variants={itemVariants} className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-center space-x-1 p-1 sm:p-2 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/60 shadow-sm w-fit mx-auto overflow-x-auto">
            {viewConfigs.map((config) => {
              const Icon = config.icon;
              const isActive = activeView === config.id;
              
              return (
                <Tooltip key={config.id}>
                  <TooltipTrigger asChild>
                    <motion.button
                      onClick={() => setActiveView(config.id)}
                      className={cn(
                        "relative px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center space-x-1 sm:space-x-2 md:space-x-3 min-w-[90px] sm:min-w-[120px] md:min-w-[140px] min-h-[44px] touch-manipulation",
                        isActive 
                          ? "bg-white shadow-lg text-white" 
                          : "text-gray-600 hover:text-gray-900 hover:bg-white/60"
                      )}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isActive && (
                        <motion.div 
                          className={cn("absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r", config.gradient)}
                          layoutId="activeTab"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                      <div className="relative z-10 flex items-center justify-center space-x-1 sm:space-x-2">
                        <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="font-medium text-xs sm:text-sm truncate">{config.name}</span>
                      </div>
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{config.description}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </motion.div>

        {/* Main Content Area - Enhanced Mobile */}
        <motion.div variants={itemVariants} className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 pb-4 sm:pb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeView}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white/40 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/60 shadow-lg overflow-hidden"
            >
              {renderCurrentView()}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Add Schedule Dialog */}
        <AddScheduleDialog
          open={isAddScheduleOpen}
          onClose={() => setIsAddScheduleOpen(false)}
          organizationId={organizationId}
          initialClassId={classId} 
          onScheduleAdded={handleScheduleAdded}
          classId={classId || ''}
          classes={classes}
        />
        
        {/* Keyboard Shortcuts Help */}
        {showKeyboardHelp && (
          <KeyboardShortcutsHelp
            shortcuts={shortcuts}
            onClose={() => setShowKeyboardHelp(false)}
          />
        )}
      </motion.div>
    </TooltipProvider>
  );
};

export default Schedule;
